import { useEffect, useState } from "react";
import { Ban, Eye, History, Printer, Search, X } from "lucide-react";
import {
  getPosReceipt,
  getPosSalesHistory,
  voidPosSale,
} from "../../../services/pos";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function statusClasses(status) {
  if (status === "RETURNED") return "bg-red-100 text-red-700";
  if (status === "PARTIALLY_RETURNED") return "bg-amber-100 text-amber-700";
  if (status === "VOIDED") return "bg-gray-200 text-gray-700";
  return "bg-green-100 text-green-700";
}

function printReceipt(receipt) {
  const itemRows = receipt.items
    .map(
      (item, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>
            <strong>${item.productNameSnapshot}</strong><br />
            <small>${item.variantNameSnapshot || "Default Variant"} · ${item.skuSnapshot}</small>
          </td>
          <td style="text-align:right">${Number(item.quantity)}</td>
          <td style="text-align:right">${money(item.unitPrice)}</td>
          <td style="text-align:right">${money(item.lineTotalAmount)}</td>
        </tr>
      `,
    )
    .join("");

  const paymentRows = receipt.payments
    .map(
      (payment) => `
        <div class="summary-row">
          <span>${payment.method.replaceAll("_", " ")}</span>
          <strong>${money(payment.amount)}</strong>
        </div>
      `,
    )
    .join("");

  const printWindow = window.open("", "_blank", "width=760,height=900");

  if (!printWindow) return;

  printWindow.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>${receipt.receiptNumber}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 28px;
            color: #172033;
            font-family: Arial, sans-serif;
            font-size: 13px;
          }
          .receipt { max-width: 720px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #7a2238; padding-bottom: 16px; }
          h1 { margin: 0; color: #7a2238; font-size: 25px; }
          h2 { margin: 8px 0 0; font-size: 15px; }
          .muted { color: #667085; margin: 5px 0; }
          .meta { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin: 18px 0; }
          .meta-box { background: #f8f6f5; border-radius: 8px; padding: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          th { background: #f2f4f7; text-align: left; padding: 9px; font-size: 11px; }
          td { border-bottom: 1px solid #e4e7ec; padding: 10px 9px; vertical-align: top; }
          .summary { width: 310px; margin: 20px 0 0 auto; }
          .summary-row { display: flex; justify-content: space-between; padding: 7px 0; }
          .total { border-top: 2px solid #7a2238; font-size: 18px; color: #7a2238; }
          .footer { margin-top: 34px; border-top: 1px dashed #98a2b3; padding-top: 16px; text-align: center; color: #667085; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1>CHARMS</h1>
            <h2>${receipt.store.name}</h2>
            <p class="muted">
              ${[
                receipt.store.addressLine1,
                receipt.store.city,
                receipt.store.state,
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
            <p class="muted">${receipt.store.phone || ""}</p>
          </div>

          <div class="meta">
            <div class="meta-box">
              <strong>Receipt</strong>
              <div>${receipt.receiptNumber}</div>
            </div>
            <div class="meta-box">
              <strong>Date</strong>
              <div>${new Date(receipt.completedAt).toLocaleString("en-IN")}</div>
            </div>
            <div class="meta-box">
              <strong>Customer</strong>
              <div>${receipt.customer?.fullName || "Walk-in Customer"}</div>
            </div>
            <div class="meta-box">
              <strong>Cashier / Counter</strong>
              <div>${receipt.createdByUser?.employee?.fullName || receipt.createdByUser?.email || "—"}</div>
              <small>${receipt.counter.name}</small>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th style="text-align:right">Qty</th>
                <th style="text-align:right">Rate</th>
                <th style="text-align:right">Amount</th>
              </tr>
            </thead>
            <tbody>${itemRows}</tbody>
          </table>

          <div class="summary">
            <div class="summary-row"><span>Subtotal</span><strong>${money(receipt.subtotalAmount)}</strong></div>
            ${
              Number(receipt.manualDiscountAmount) > 0
                ? `<div class="summary-row"><span>Manual Discount</span><strong>-${money(receipt.manualDiscountAmount)}</strong></div>`
                : ""
            }
            ${
              Number(receipt.couponDiscountAmount) > 0
                ? `<div class="summary-row"><span>Coupon (${receipt.couponCode})</span><strong>-${money(receipt.couponDiscountAmount)}</strong></div>`
                : ""
            }
            ${
              Number(receipt.loyaltyDiscountAmount) > 0
                ? `<div class="summary-row"><span>Loyalty Discount</span><strong>-${money(receipt.loyaltyDiscountAmount)}</strong></div>`
                : ""
            }
            <div class="summary-row"><span>GST Included</span><strong>${money(receipt.taxAmount)}</strong></div>
            <div class="summary-row total"><span>Total</span><strong>${money(receipt.totalAmount)}</strong></div>
            ${paymentRows}
          </div>

          <div class="footer">
            <p>Thank you for shopping with Charms.</p>
            <p>This is a system-generated receipt.</p>
          </div>
        </div>
        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          };
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
}

export default function PosTransactionHistoryModal({
  open,
  onClose,
  accessToken,
  canVoid = false,
  onSaleVoided,
}) {
  const [search, setSearch] = useState("");
  const [sales, setSales] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [error, setError] = useState("");
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [externalReversalReference, setExternalReversalReference] =
    useState("");
  const [voiding, setVoiding] = useState(false);

  const loadHistory = async (query = "") => {
    setLoading(true);
    setError("");

    try {
      const data = await getPosSalesHistory(query, accessToken);
      setSales(data);
    } catch (requestError) {
      setError(
        requestError.message || "Unable to load POS transaction history",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadHistory();
    }
  }, [open]);

  const handleVoidSale = async () => {
    if (!receipt) return;

    if (!voidReason.trim()) {
      setError("Enter a reason for voiding this sale");
      return;
    }

    const needsExternalReference = receipt.payments.some(
      (payment) => payment.method !== "CASH",
    );

    if (needsExternalReference && !externalReversalReference.trim()) {
      setError(
        "Enter the UPI/card/bank reversal reference before voiding this sale",
      );
      return;
    }

    setVoiding(true);
    setError("");

    try {
      const voidedSale = await voidPosSale(
        receipt.id,
        {
          reason: voidReason.trim(),
          externalReversalReference:
            externalReversalReference.trim() || undefined,
        },
        accessToken,
      );

      setReceipt((previous) => ({
        ...previous,
        status: voidedSale.status,
        voidReason: voidedSale.voidReason,
        voidedAt: voidedSale.voidedAt,
      }));

      setShowVoidModal(false);
      setVoidReason("");
      setExternalReversalReference("");

      await loadHistory(search);

      onSaleVoided?.(voidedSale);
    } catch (requestError) {
      setError(requestError.message || "Unable to void this sale");
    } finally {
      setVoiding(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    const timer = setTimeout(() => {
      loadHistory(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, open]);

  const viewReceipt = async (receiptNumber) => {
    setLoadingReceipt(true);
    setError("");

    try {
      const data = await getPosReceipt(receiptNumber, accessToken);
      setReceipt(data);
    } catch (requestError) {
      setError(requestError.message || "Unable to load receipt");
    } finally {
      setLoadingReceipt(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
      <div className="mx-auto my-6 w-full max-w-6xl rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-primary-50 text-primary-800">
              <History size={21} />
            </div>

            <div>
              <h2 className="text-2xl font-bold">Transaction History</h2>
              <p className="text-sm text-gray-500">
                Current store receipts and reprints.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-7">
          {error && (
            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="relative">
            <Search
              size={19}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search receipt number, customer name, or phone..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4 outline-none focus:border-primary-800"
            />
          </div>

          <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
            <div className="max-h-[460px] overflow-auto">
              <table className="w-full min-w-[850px] text-sm">
                <thead className="sticky top-0 bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-4 py-3">Receipt</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Counter</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3 text-center">Items</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-16 text-center text-gray-500"
                      >
                        Loading transactions…
                      </td>
                    </tr>
                  ) : sales.length ? (
                    sales.map((sale) => (
                      <tr
                        key={sale.id}
                        className="border-t border-slate-100 hover:bg-primary-50/40"
                      >
                        <td className="px-4 py-4 font-mono text-xs font-bold text-primary-800">
                          {sale.receiptNumber}
                        </td>

                        <td className="px-4 py-4">
                          <p className="font-semibold text-gray-900">
                            {sale.customer?.fullName || "Walk-in Customer"}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {sale.customer?.phone || "—"}
                          </p>
                        </td>

                        <td className="px-4 py-4 text-gray-600">
                          {sale.counter.name}
                        </td>

                        <td className="px-4 py-4 text-gray-600">
                          {new Date(sale.completedAt).toLocaleString("en-IN", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </td>

                        <td className="px-4 py-4 text-center font-semibold">
                          {sale._count.items}
                        </td>

                        <td className="px-4 py-4 text-right font-bold text-primary-800">
                          {money(sale.totalAmount)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-bold ${statusClasses(
                              sale.status,
                            )}`}
                          >
                            {sale.status.replaceAll("_", " ")}
                          </span>
                        </td>

                        <td className="px-4 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => viewReceipt(sale.receiptNumber)}
                            className="inline-flex items-center gap-2 rounded-xl bg-primary-50 px-3 py-2 text-xs font-bold text-primary-800 hover:bg-primary-100"
                          >
                            <Eye size={15} />
                            View / Print
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="py-16 text-center text-gray-500"
                      >
                        No transactions found at this store.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {receipt && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
          <div className="mx-auto my-6 w-full max-w-3xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-7 py-5">
              <div>
                <h2 className="text-xl font-bold">Receipt Preview</h2>
                <p className="mt-1 text-sm text-gray-500">
                  {receipt.receiptNumber}
                </p>
              </div>

              <div className="flex gap-2">
                {canVoid && receipt.status === "COMPLETED" && (
                  <button
                    type="button"
                    onClick={() => setShowVoidModal(true)}
                    className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    <Ban size={16} />
                    Void Sale
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => printReceipt(receipt)}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-2 text-sm font-semibold text-white"
                >
                  <Printer size={16} />
                  Print
                </button>

                <button
                  type="button"
                  onClick={() => setReceipt(null)}
                  className="grid h-10 w-10 place-items-center rounded-xl bg-gray-100 text-gray-600"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {loadingReceipt ? (
              <div className="p-16 text-center text-gray-500">
                Loading receipt…
              </div>
            ) : (
              <div className="p-7">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-primary-800">
                    CHARMS
                  </h3>
                  <p className="mt-1 font-semibold">{receipt.store.name}</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {[
                      receipt.store.addressLine1,
                      receipt.store.city,
                      receipt.store.state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4 rounded-2xl bg-[#F8F6F5] p-4 text-sm">
                  <div>
                    <p className="text-gray-500">Customer</p>
                    <p className="mt-1 font-semibold">
                      {receipt.customer?.fullName || "Walk-in Customer"}
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-500">Counter</p>
                    <p className="mt-1 font-semibold">{receipt.counter.name}</p>
                  </div>

                  <div>
                    <p className="text-gray-500">Cashier</p>
                    <p className="mt-1 font-semibold">
                      {receipt.createdByUser?.employee?.fullName ||
                        receipt.createdByUser?.email ||
                        "—"}
                    </p>
                  </div>

                  <div>
                    <p className="text-gray-500">Sale date</p>
                    <p className="mt-1 font-semibold">
                      {new Date(receipt.completedAt).toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>

                <div className="mt-6 overflow-hidden rounded-2xl border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
                      <tr>
                        <th className="px-4 py-3">Item</th>
                        <th className="px-4 py-3 text-center">Qty</th>
                        <th className="px-4 py-3 text-right">Rate</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                      </tr>
                    </thead>

                    <tbody>
                      {receipt.items.map((item) => (
                        <tr key={item.id} className="border-t">
                          <td className="px-4 py-3">
                            <p className="font-semibold">
                              {item.productNameSnapshot}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              {item.variantNameSnapshot || "Default Variant"} ·{" "}
                              {item.skuSnapshot}
                            </p>
                          </td>

                          <td className="px-4 py-3 text-center">
                            {Number(item.quantity)}
                          </td>

                          <td className="px-4 py-3 text-right">
                            {money(item.unitPrice)}
                          </td>

                          <td className="px-4 py-3 text-right font-bold text-primary-800">
                            {money(item.lineTotalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="ml-auto mt-6 max-w-sm space-y-3 rounded-2xl bg-[#F8F6F5] p-5">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Subtotal</span>
                    <b>{money(receipt.subtotalAmount)}</b>
                  </div>

                  {Number(receipt.couponDiscountAmount) > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Coupon ({receipt.couponCode})</span>
                      <b>-{money(receipt.couponDiscountAmount)}</b>
                    </div>
                  )}

                  {Number(receipt.loyaltyDiscountAmount) > 0 && (
                    <div className="flex justify-between text-green-700">
                      <span>Loyalty discount</span>
                      <b>-{money(receipt.loyaltyDiscountAmount)}</b>
                    </div>
                  )}

                  <div className="flex justify-between border-t border-gray-200 pt-3 text-xl font-bold">
                    <span>Total</span>
                    <span className="text-primary-800">
                      {money(receipt.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showVoidModal && receipt && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl bg-white p-7 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-red-50 text-red-600">
                  <Ban size={21} />
                </div>

                <h2 className="mt-4 text-2xl font-bold text-gray-900">
                  Void Sale
                </h2>

                <p className="mt-2 text-sm text-gray-500">
                  This will reverse stock, cash, coupon usage, and loyalty
                  effects for <b>{receipt.receiptNumber}</b>.
                </p>
              </div>

              <button
                type="button"
                disabled={voiding}
                onClick={() => setShowVoidModal(false)}
                className="grid h-10 w-10 place-items-center rounded-xl bg-gray-100 text-gray-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Use this only for an accidental bill in the current open shift.
              For physically returned products, use the POS Return workflow.
            </div>

            <label className="mt-6 block text-sm font-semibold text-gray-700">
              Void Reason *
            </label>

            <textarea
              rows={4}
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              placeholder="Explain why this sale is being voided..."
              className="mt-2 w-full rounded-2xl border border-gray-200 p-4 outline-none focus:border-primary-800"
            />

            {receipt.payments.some((payment) => payment.method !== "CASH") && (
              <>
                <label className="mt-5 block text-sm font-semibold text-gray-700">
                  UPI / Card / Bank Reversal Reference *
                </label>

                <input
                  value={externalReversalReference}
                  onChange={(event) =>
                    setExternalReversalReference(event.target.value)
                  }
                  placeholder="Enter reversal or refund reference"
                  className="mt-2 h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-800"
                />
              </>
            )}

            <div className="mt-7 flex gap-3">
              <button
                type="button"
                disabled={voiding}
                onClick={() => setShowVoidModal(false)}
                className="flex-1 rounded-xl border border-gray-200 py-3 font-semibold text-gray-700"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={voiding || !voidReason.trim()}
                onClick={handleVoidSale}
                className="flex-1 rounded-xl bg-red-600 py-3 font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {voiding ? "Voiding…" : "Confirm Void"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
